import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NavController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {
  Observable,
  combineLatest,
  distinctUntilChanged,
  from,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { SignupReq } from 'src/app/api/contracts/auth';
import {
  ClientCard,
  Profile,
  UpdateProfileReq,
} from 'src/app/api/contracts/client';
import { DeliveryLocation } from 'src/app/api/contracts/location';
import { AppDropdownModel } from 'src/app/components/UI/dropdown/dropdown.types';
import { TabsModel } from 'src/app/components/UI/tabs/tabs.types';
import { APP_CONFIG, AppConfig } from 'src/app/config/app-config';
import { ClientService } from 'src/app/services/client.service';
import {
  AppLanguage,
  LanguageService,
} from 'src/app/services/language.service';
import { LocationService } from 'src/app/services/location.service';
import { PermissionService } from 'src/app/services/permission.service';
import { StorageKeys, StorageService } from 'src/app/services/storage.service';
import { ToastService } from 'src/app/services/toast.service';
import { AppPermissions } from 'src/app/types/permissions';
import { LoaderController } from 'src/app/utils/loader-controller';
import { confirmPasswordFormValidator } from 'src/app/validators/confirm-password.validator';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit {
  public readonly profile$: Observable<Profile>;
  public readonly clientLocation$: Observable<DeliveryLocation>;
  public readonly clientCard$: Observable<ClientCard | null | false>;

  public readonly profileForm: FormGroup<Record<keyof SignupReq, FormControl>>;
  public readonly loader = new LoaderController();

  public locationDropdownModel: AppDropdownModel = {
    options: [],
  };

  public readonly tabsModel: TabsModel = {
    tabs: [
      {
        index: 0,
        i18nLabel: '@pages.profile.tabs.myProfile',
      },
      {
        index: 1,
        i18nLabel: '@pages.profile.tabs.settings',
      },
    ],
    activeTabIndex: 0,
  };

  public readonly permissions = AppPermissions;

  constructor(
    @Inject(APP_CONFIG) private readonly appConfig: AppConfig,
    private readonly clientService: ClientService,
    private readonly storageService: StorageService,
    private readonly languageService: LanguageService,
    private readonly translationService: TranslateService,
    private readonly locationService: LocationService,
    private readonly toastService: ToastService,
    private readonly navCtrl: NavController,
    public readonly permissionService: PermissionService,
  ) {
    this.profile$ = this.clientService.profile$;
    this.clientCard$ = this.clientService.clientCard$;
    this.clientLocation$ = this.clientService.clientLocation$;
    this.profileForm = this.createForm();
    this.profileForm.setValidators(
      confirmPasswordFormValidator('password', 'confirmPassword')
    );
  }

  ngOnInit() {
    this.loadProfile();
    this.clientService.retrieveClientCard().subscribe();
    this.initLocationDropdownModelOptions();
  }

  onSelectTab(tabIndex: number): void {
    this.tabsModel.activeTabIndex = tabIndex;
  }

  onEditCreditCardClick(): void {
    this.clientService
      .openCardEditingDashboard()
      .pipe(switchMap(() => this.clientService.retrieveClientCard()))
      .subscribe(() => {});
  }

  onUpdateProfile(): void {
    this.loader.enqueue();
    this.profileForm.disable();

    const updateBody: UpdateProfileReq = {
      email: this.profileForm.value.email,
      name: this.profileForm.value.name,
      pickupHandle: this.profileForm.value.pickupHandle,
      password: this.profileForm.value.password,
      confirmPassword: this.profileForm.value.confirmPassword,
    };
    if (!updateBody.password || !updateBody.confirmPassword) {
      delete updateBody.password;
      delete updateBody.confirmPassword;
    }
    combineLatest([
      this.clientService.updateProfile(updateBody),
      this.clientService.updateClientLocation(
        this.profileForm.value.locationHash
      ),
    ]).subscribe({
      next: ([profile, deliveryLocation]) => {
        this.loader.dequeue();
        this.profileForm.enable();
        this.prefillProfileData(profile);
        this.prefillUserLocation(deliveryLocation);
        this.toastService
          .showNotification({
            message: this.translationService.instant(
              '@pages.profile.messages.update.success'
            ),
          })
          .subscribe();
      },
      error: (err) => {
        this.loader.dequeue();
        this.profileForm.enable();
        this.toastService
          .showNotification({
            message: err,
          })
          .subscribe();
      },
    });
  }

  onBackButtonClick(): void {
    from(this.navCtrl.pop()) // pop() already makes back navigation
      .pipe(take(1))
      .subscribe((hasHistory) => {
        if (!hasHistory) {
          this.navCtrl.navigateRoot('/orders');
        }
      });
  }

  private loadProfile(): void {
    this.profileForm.disable();
    this.loader.enqueue();
    this.clientService.profile$.subscribe((profile) => {
      this.profileForm.enable();
      this.loader.dequeue();
      this.prefillProfileData(profile);
    });
    this.clientService.retrieveProfile().subscribe();
  }

  private createForm(): FormGroup {
    return new FormGroup({
      name: new FormControl('', [
        Validators.required,
        Validators.minLength(this.appConfig.fieldValidation.fullname.minLength),
        Validators.maxLength(64),
        Validators.pattern(this.appConfig.fieldValidation.fullname.pattern),
      ]),
      email: new FormControl('', [
        Validators.required,
        Validators.maxLength(64),
        Validators.pattern(this.appConfig.fieldValidation.email.pattern),
      ]),
      pickupHandle: new FormControl('', [
        Validators.required,
        Validators.minLength(this.appConfig.fieldValidation.username.minLength),
        Validators.maxLength(64),
        Validators.pattern(this.appConfig.fieldValidation.username.pattern),
      ]),
      password: new FormControl(null, [
        Validators.minLength(this.appConfig.fieldValidation.password.minLength),
        Validators.maxLength(64),
        Validators.pattern(this.appConfig.fieldValidation.password.pattern),
      ]),
      confirmPassword: new FormControl(null, [
        Validators.minLength(this.appConfig.fieldValidation.password.minLength),
        Validators.maxLength(64),
        Validators.pattern(this.appConfig.fieldValidation.password.pattern),
      ]),
      locationHash: new FormControl(null, [Validators.required]),
    });
  }

  private initLocationDropdownModelOptions(): void {
    this.profileForm.controls.locationHash.disable();
    this.locationService
      .retrieveLocations()
      .pipe(
        tap((locations) => {
          {
            this.locationDropdownModel.options = locations.map((location) => {
              return {
                value: location.hash,
                label: location.name,
              };
            });
          }
        }),
        switchMap(() => this.clientService.clientLocation$)
      )
      .subscribe((location) => {
        this.prefillUserLocation(location);
        this.profileForm.controls.locationHash.enable();
      });
  }

  private prefillProfileData(profile: Profile): void {
    this.profileForm.controls.name.setValue(profile.name);
    this.profileForm.controls.email.setValue(profile.email);
    this.profileForm.controls.pickupHandle.setValue(profile.pickupHandle);
  }

  private prefillUserLocation(location: DeliveryLocation): void {
    this.profileForm.controls.locationHash.setValue(location.hash);
  }
}
